'use client';

import React, { createContext, useCallback, useContext, useState, useMemo } from 'react';

interface ExternalAreaLinkContextValue {
  externalAreaLink: string | null;
  setExternalAreaLink: (link: string | null) => void;
  isDismissed: boolean;
  dismiss: () => void;
}

const ExternalAreaLinkContext = createContext<ExternalAreaLinkContextValue>({
  externalAreaLink: null,
  setExternalAreaLink: () => {},
  isDismissed: false,
  dismiss: () => {},
});

export function useExternalAreaLink(): string | null {
  return useContext(ExternalAreaLinkContext).externalAreaLink;
}

export function useSetExternalAreaLink(): (link: string | null) => void {
  return useContext(ExternalAreaLinkContext).setExternalAreaLink;
}

export function useExternalAreaLinkDismissed(): { isDismissed: boolean; dismiss: () => void } {
  const { isDismissed, dismiss } = useContext(ExternalAreaLinkContext);
  return { isDismissed, dismiss };
}

export function ExternalAreaLinkProvider({
  children,
  initialValue = null,
}: {
  children: React.ReactNode;
  initialValue?: string | null;
}) {
  const [externalAreaLink, setExternalAreaLink] = useState<string | null>(initialValue);
  const [isDismissed, setIsDismissed] = useState(false);
  const dismiss = useCallback(() => setIsDismissed(true), []);

  const value = useMemo(
    () => ({ externalAreaLink, setExternalAreaLink, isDismissed, dismiss }),
    [externalAreaLink, isDismissed, dismiss],
  );

  return (
    <ExternalAreaLinkContext.Provider value={value}>{children}</ExternalAreaLinkContext.Provider>
  );
}
